import { createTheme } from '@mui/material/styles';
import { DocumentTagID } from '@karehero/models';

type Color = {
  main: string;
  light?: string;
  dark?: string;
  contrastText?: string;
};

export type ColorScale = {
  50: string;
  100: string;
  200: string;
  300: string;
  400: string;
  500: string;
  600: string;
  700: string;
  800: string;
  900: string;
};

export const Coral: ColorScale = {
  50: '#F9EBE8',
  100: '#FAD0C1',
  200: '#F8B299',
  300: '#F79570',
  400: '#F57F51',
  500: '#F56B34',
  600: '#EA6430',
  700: '#DC5E2C',
  800: '#CE5729',
  900: '#B34C24',
};

export const MarineGreen: ColorScale = {
  50: '#7BC4C5',
  100: '#46ADAD',
  200: '#1B9B9A',
  300: '#008A87',
  400: '#007E7A',
  500: '#006E6A',
  600: '#005E5A',
  700: '#00433D',
  800: '#003330',
  900: '#001E1B',
};

export const LimeGreen: ColorScale = {
  50: '#FBFCF6',
  100: '#F4F7E8',
  200: '#EDF3DA',
  300: '#E4ECC8',
  400: '#DBE6B5',
  500: '#D2E0A3',
  600: '#A8B382',
  700: '#7E8662',
  800: '#545A41',
  900: '#2A2D21',
};

export const Sunflower: ColorScale = {
  50: '#FFFCF0',
  100: '#FFF7DA',
  200: '#FFF1C4',
  300: '#FFEBA7',
  400: '#FFE489',
  500: '#FFDD6C',
  600: '#CCB156',
  700: '#998541',
  800: '#66582B',
  900: '#332C16',
};

export const Neutral: ColorScale = {
  50: '#FFFCF5',
  100: '#F4F1EA',
  200: '#E1DED8',
  300: '#C6C3BC',
  400: '#A3A09A',
  500: '#817E78',
  600: '#63605B',
  700: '#484540',
  800: '#312E29',
  900: '#1A1712',
};

export const WarmOrange: ColorScale = {
  50: '#FDF4EC',
  100: '#FAE3D0',
  200: '#F7D3B4',
  300: '#F4BC8F',
  400: '#F0A669',
  500: '#EC9044',
  600: '#BD7336',
  700: '#8E5629',
  800: '#5E3A1B',
  900: '#2F1D0E',
};

export const Cherry: ColorScale = {
  50: '#FAF0F4',
  100: '#F4D7E3',
  200: '#E8B0C6',
  300: '#D2799D',
  400: '#B1436F',
  500: '#732F4A',
  600: '#5C263B',
  700: '#451C2C',
  800: '#2E131E',
  900: '#17090F',
};

export const Lavender: ColorScale = {
  50: '#F0EDFC',
  100: '#DED7F9',
  200: '#CBC0F6',
  300: '#B9AAF3',
  400: '#A694F0',
  500: '#9681EA',
  600: '#7867BB',
  700: '#5A4D8C',
  800: '#3C345E',
  900: '#1E1A2F',
};

export const Gardenia = '#F0EAE0';
export const Charcoal = '#404042';

export const KareHeroColors = {
  coral: Coral,
  marineGreen: MarineGreen,
  limeGreen: LimeGreen,
  sunflower: Sunflower,
  neutral: Neutral,
  warmOrange: WarmOrange,
  cherry: Cherry,
  lavender: Lavender,
  gardenia: Gardenia,
  charcoal: Charcoal,
};

export const Green: string = MarineGreen[900];

export const Primary: Color = {
  main: MarineGreen[700],
  dark: MarineGreen[800],
  light: MarineGreen[50],
  contrastText: '#FFFFFF',
};

export const Secondary: Color = {
  main: Coral[700],
  dark: Coral[900],
  light: Coral[200],
  contrastText: '#FFFFFF',
};

// Brand-color palette entries, registered as proper MUI palette tokens so
// MUI components (Chip, Button, etc.) can address them via `color='cherry'`,
// `color='warmOrange'`, etc.
export const CherryColor: Color = {
  main: Cherry[500],
  dark: Cherry[700],
  light: Cherry[200],
  contrastText: '#FFFFFF',
};
export const WarmOrangeColor: Color = {
  main: WarmOrange[500],
  dark: WarmOrange[700],
  light: WarmOrange[200],
  contrastText: '#FFFFFF',
};
export const LimeGreenColor: Color = {
  main: LimeGreen[700],
  dark: LimeGreen[800],
  light: LimeGreen[300],
  contrastText: '#FFFFFF',
};
export const LavenderColor: Color = {
  main: Lavender[500],
  dark: Lavender[700],
  light: Lavender[200],
  contrastText: '#FFFFFF',
};
export const SunflowerColor: Color = {
  main: Sunflower[500],
  dark: Sunflower[700],
  light: Sunflower[200],
  contrastText: MarineGreen[800],
};

export const TextPrimary: string = Charcoal;
export const TextSecondary: string = Neutral[600];
export const TextDisabled: string = Neutral[500];
export const TextTitle: string = MarineGreen[700];
export const TextSubtitle: string = MarineGreen[600];

export const color = {
  primary: Primary,
  secondary: Secondary,
  cherry: CherryColor,
  warmOrange: WarmOrangeColor,
  limeGreen: LimeGreenColor,
  lavender: LavenderColor,
  sunflower: SunflowerColor,
  text: {
    primary: TextPrimary,
    secondary: TextSecondary,
    disabled: TextDisabled,
    title: TextTitle,
    subtitle: TextSubtitle,
  },
  background: {
    default: Gardenia,
    paper: Neutral[50],
  },
  divider: Neutral[300],
  custom: {
    ...KareHeroColors,
  },
} as const;

declare module '@mui/material/styles' {
  interface Palette {
    custom: typeof KareHeroColors;
    cherry: Palette['primary'];
    warmOrange: Palette['primary'];
    limeGreen: Palette['primary'];
    lavender: Palette['primary'];
    sunflower: Palette['primary'];
  }
  interface PaletteOptions {
    custom?: typeof KareHeroColors;
    cherry?: PaletteOptions['primary'];
    warmOrange?: PaletteOptions['primary'];
    limeGreen?: PaletteOptions['primary'];
    lavender?: PaletteOptions['primary'];
    sunflower?: PaletteOptions['primary'];
  }
  interface TypeText {
    title: string;
    subtitle: string;
  }
}

declare module '@mui/material/Chip' {
  interface ChipPropsColorOverrides {
    cherry: true;
    warmOrange: true;
    limeGreen: true;
    lavender: true;
    sunflower: true;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    cherry: true;
    warmOrange: true;
    limeGreen: true;
    lavender: true;
    sunflower: true;
  }
}

declare module '@mui/material/Avatar' {
  interface AvatarOwnProps {
    // MUI's Avatar has no `color` prop by default; we add one so the theme's
    // colour variants (see MuiAvatar in components.ts) can be addressed.
    color?:
      | 'primary'
      | 'secondary'
      | 'cherry'
      | 'warmOrange'
      | 'limeGreen'
      | 'lavender'
      | 'sunflower';
  }
}

export const themeColor = createTheme({ palette: color });

const DEFAULT_TAG_COLOR = Neutral[500];

export const documentTagColors: Record<DocumentTagID, string> = {
  [DocumentTagID.Funding]: MarineGreen[300],
  [DocumentTagID.FindCare]: WarmOrange[500],
  [DocumentTagID.PowerOfAttorney]: Lavender[500],
  [DocumentTagID.CallNotes]: Coral[500],
  [DocumentTagID.Onboarding]: Neutral[500],
};

export const getDocumentTagColor = (tagId: string): string =>
  documentTagColors[tagId as DocumentTagID] ?? DEFAULT_TAG_COLOR;
